import type { BoxProps } from '@montugroup/design-system';
import { Box, Stack, Tab, Tabs, Typography } from '@montugroup/design-system';
import { styled } from '@mui/material';
import type { ReactNode } from 'react';

import AllOrdersTable from '@/components/orders/tables/AllOrdersTable';
import OpenOrdersTables from '@/components/orders/tables/delivery-tables/open-orders/OpenOrdersTables';
import DispensedOrdersTable from '@/components/orders/tables/DispensedOrdersTable';
import { PharmacistOrderTableView, usePharmacistOrders } from '@/context/orders/PharmacistOrders';
import { ListOrdersView } from '@/hooks/orders/useListOrders';
import { getTsPharmacyClient } from '@montugroup/circuit-shared/api/pharmacy.client';
import { useClearSelectedOrders } from '@montugroup/circuit-shared/context/table/OrderSelectionContext';
import getAuthBearerToken from '@montugroup/circuit-shared/utils/getAuthBearerToken';
import { TabContext, TabPanel } from '@mui/lab';
import { OrdersPendingCollectionAlert } from './tables/delivery-tables/OrdersPendingCollectionAlert';
import PendingCollectionTable from './tables/delivery-tables/pending-collection/PendingCollectionTable';
import ProcessingDispenseTables from './tables/delivery-tables/processing-dispense/ProcessingDispenseTables';

const client = getTsPharmacyClient();

const StandaloneBadge = ({
  children,
  color,
  backgroundColor,
}: {
  children: ReactNode;
  color: BoxProps['color'];
  backgroundColor: BoxProps['bgcolor'];
}) => {
  return (
    <Box
      sx={{
        height: 18,
        minWidth: 26,
        px: 1,
        borderRadius: '10px',
        backgroundColor,
        color,
        fontSize: '0.75rem',
        fontWeight: 600,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        lineHeight: 1,
      }}
    >
      {children}
    </Box>
  );
};

const TabBadge = ({ isTabActive, children }: { isTabActive: boolean; children: ReactNode }) => {
  return (
    <StandaloneBadge
      color={isTabActive ? 'secondary.contrastText' : 'primary.contrastText'}
      backgroundColor={isTabActive ? 'secondary.main' : 'primary.main'}
    >
      {children}
    </StandaloneBadge>
  );
};

const PharmacistTabLabel = styled(Typography)(({ theme }) => ({
  textTransform: 'none',
  fontFamily: theme.typography.button.fontFamily,
  '&:focus': {
    outline: 'none',
  },
  '&:focus-visible': {
    outline: 'auto',
  },
}));

function PharmacistOrderTables() {
  const { data } = client.listOrders.useQuery({
    queryKey: [...client.listOrders.keys, ListOrdersView.PendingCollection],
    queryData: {
      query: {
        view: ListOrdersView.PendingCollection,
      },
      headers: {
        authorization: getAuthBearerToken(),
      },
    },
  });
  const { activeView, changeTableView } = usePharmacistOrders();
  const clearSelectedOrders = useClearSelectedOrders();

  const pendingCollectionCount = data?.body.data.ClickAndCollect?.length;

  const handleChange = (_event: any, newView: PharmacistOrderTableView) => {
    changeTableView(newView);
    clearSelectedOrders();
  };

  return (
    <TabContext value={activeView}>
      <Tabs value={activeView} onChange={handleChange} textColor="secondary" indicatorColor="secondary">
        <Tab
          label={<PharmacistTabLabel variant="button">All</PharmacistTabLabel>}
          value={PharmacistOrderTableView.ALL_ORDERS}
        />
        <Tab
          label={<PharmacistTabLabel variant="button">Open</PharmacistTabLabel>}
          value={PharmacistOrderTableView.OPEN_ORDERS}
        />
        <Tab
          label={<PharmacistTabLabel variant="button">Processing</PharmacistTabLabel>}
          value={PharmacistOrderTableView.PROCESSING_DISPENSE}
        />
        <Tab
          label={<PharmacistTabLabel variant="button">Dispensed</PharmacistTabLabel>}
          value={PharmacistOrderTableView.DISPENSED_ORDERS}
        />
        <Tab
          label={
            <Stack direction={'row'} justifyContent={'center'} alignItems={'center'} gap={2}>
              <PharmacistTabLabel variant="button">Pending Collection</PharmacistTabLabel>
              {!!pendingCollectionCount && (
                <TabBadge isTabActive={activeView === PharmacistOrderTableView.PENDING_COLLECTION}>
                  {pendingCollectionCount}
                </TabBadge>
              )}
            </Stack>
          }
          value={PharmacistOrderTableView.PENDING_COLLECTION}
        />
      </Tabs>
      <OrdersPendingCollectionAlert
        activeView={activeView}
        ordersPendingCollection={pendingCollectionCount}
        goToPendingCollectionTab={() => handleChange(undefined, PharmacistOrderTableView.PENDING_COLLECTION)}
      />
      <TabPanel value={PharmacistOrderTableView.ALL_ORDERS}>
        <AllOrdersTable />
      </TabPanel>
      <TabPanel value={PharmacistOrderTableView.OPEN_ORDERS}>
        <OpenOrdersTables />
      </TabPanel>
      <TabPanel value={PharmacistOrderTableView.PROCESSING_DISPENSE}>
        <ProcessingDispenseTables />
      </TabPanel>
      <TabPanel value={PharmacistOrderTableView.DISPENSED_ORDERS}>
        <DispensedOrdersTable />
      </TabPanel>
      <TabPanel value={PharmacistOrderTableView.PENDING_COLLECTION}>
        <PendingCollectionTable />
      </TabPanel>
    </TabContext>
  );
}

export default PharmacistOrderTables;
